// M-C — Shim `toast()` shadcn API into the actually-mounted `sonner`
// Toaster. Prior to this shim, every `toast({title, description,
// variant})` call in ClinicMessagesV2 (and any other page using this
// hook) was routed to the shadcn Toaster component — which is NOT
// mounted in App.js. Result: silent failures on 403/400 send errors.
//
// This shim maps the shadcn API 1:1 onto sonner's imperative API:
//   toast({variant:'destructive', title, description})  → sonner.error(title, {description})
//   toast({title, description})                          → sonner.success(title, {description})
//   toast('plain string')                                → sonner(msg)

"use client";
import * as React from "react";
import { toast as sonnerToast } from "sonner";

function toast(props) {
  // Plain-string shorthand
  if (typeof props === "string") {
    sonnerToast(props);
    return { id: props, dismiss: () => {}, update: () => {} };
  }
  const { title, description, variant, action, ...rest } = props || {};
  const message = title || description || "";
  const opts = {};
  if (title && description) opts.description = description;
  if (action) opts.action = action;
  Object.assign(opts, rest);

  let id;
  if (variant === "destructive") {
    id = sonnerToast.error(message, opts);
  } else if (variant === "success") {
    id = sonnerToast.success(message, opts);
  } else if (variant === "warning") {
    id = sonnerToast.warning(message, opts);
  } else if (variant === "info") {
    id = sonnerToast.info(message, opts);
  } else {
    id = sonnerToast(message, opts);
  }
  return {
    id,
    dismiss: () => sonnerToast.dismiss(id),
    update: (next) => sonnerToast.message(next?.title || next?.description || "", next),
  };
}

// Kept for API parity — no toast list state is needed with sonner.
function useToast() {
  return {
    toasts: [],
    toast,
    dismiss: (toastId) => sonnerToast.dismiss(toastId),
  };
}

export { useToast, toast };
